import { useGSAP } from '@gsap/react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player/lazy';
import PlayIcon from '../svgs/PlayIcon';
import VrIcon from '../svgs/VrIcon';

import {
  coverWrapper,
  imgWrapper,
  loadingSpinner,
  playBtnWrapper,
  show,
} from './VideoPlayer.module.scss';

gsap.registerPlugin(ScrollTrigger);

const VideoPlayer = ({
  cover,
  url,
  localVideoUrl,
  wrapperClass,
  autoplay,
  useCoverSize,
  isVr,
  disableAutoPause,
  playHomeFeatured,
  projectSlug,
}) => {
  if (autoplay === null) autoplay = false;
  const [videoUrl, setVideoUrl] = useState(url);
  const [videoReady, setVideoReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showCover, setShowCover] = useState(true);
  const [lazyLoad, setLazyLoad] = useState(false);

  useEffect(() => {
    // Load offline video link
    if (process.env.GATSBY_LOCAL === 'true' && localVideoUrl) {
      setVideoUrl(`/local_videos/${localVideoUrl}`);
    }
  }, []);

  const handlePlayBtn = () => {
    if (!autoplay) {
      setShowCover(false);
      setIsPlaying(!isPlaying);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handlePlayBtn();
    }
  };

  const videoContainer = useRef();
  const scrollTriggerRef = useRef();
  const spinnerRef = useRef();

  // Lazy Loading the Vimeo URLs, to reduce calls and data when the page loads
  useGSAP(() => {
    scrollTriggerRef.current = ScrollTrigger.create({
      trigger: videoContainer.current,
      start: 'top bottom+=100%', // trying to get a head start on loading the videos
      end: 'bottom top',
      // markers: true,
      onEnter: () => {
        setLazyLoad(true);
      },
      once: true,
    });
  }, []);

  // Play
  useGSAP(() => {
    if (!disableAutoPause) {
      scrollTriggerRef.current = ScrollTrigger.create({
        trigger: videoContainer.current,
        start: 'top bottom+=50%', // trying to get a head start on loading the videos
        end: 'bottom top',
        // markers: true,
        onEnter: () => {
          if (autoplay) setIsPlaying(true);
        },
        onEnterBack: () => {
          if (autoplay) setIsPlaying(true);
        },
        onLeave: () => setIsPlaying(false),
        onLeaveBack: () => setIsPlaying(false),
      });
    }
  }, []);

  // Spinner animation
  const spinnerTween = useRef();
  useGSAP(
    () => {
      if (process.env.GATSBY_LOCAL === 'true' && !localVideoUrl) {
        // hide spinner if local videos and no video available
        spinnerTween.current = gsap.set(spinnerRef.current, {
          autoAlpha: 0,
        });
      } else {
        gsap.config({ nullTargetWarn: false });
        spinnerTween.current = gsap.to(spinnerRef.current, {
          rotate: 360,
          duration: 1,
          repeat: -1,
          ease: 'none',
        });
      }
    },
    { dependencies: [] }
  );

  // onReady callback.
  // set the ready state, and play autoplay videos if they're in the viewport
  const setActiveAutoplay = () => {
    setVideoReady(true);
    // disableAutoPause fixes conflicts with the homepage cards
    if (autoplay && !disableAutoPause && scrollTriggerRef.current.isActive) {
      setIsPlaying(true);
    }
  };

  // For autoplay videos, fade out the cover image as soon as they're ready, not when they start
  // The delayedCall keeps the spinner spinning while it fades out
  useEffect(() => {
    autoplay && videoReady && setShowCover(false);
    videoReady && gsap.delayedCall(1, () => spinnerTween.current.pause());
  }, [autoplay, videoReady]);

  // The homepage cards have their on playing ScrollTrigger
  useEffect(() => {
    setIsPlaying(playHomeFeatured);
  }, [playHomeFeatured]);

  return (
    <div
      className={`posRel ${wrapperClass ? wrapperClass : ''}`}
      ref={videoContainer}
    >
      {/* TODO Can (should?) we prevent the video iframe from getting focus on autoplay videos? */}
      <ReactPlayer
        url={lazyLoad ? videoUrl : null}
        playing={isPlaying}
        className={`mediaBoxInner`}
        // Hacky thing to make videos always fill the container height. Could be bigger than 200% if it's still an issue
        width="200%"
        height="100.1%" // trying to prevent gaps due to pixel rounding errors
        controls={!autoplay}
        loop={autoplay}
        volume={autoplay ? 0 : 1}
        muted={autoplay}
        playsinline={true} // stop ios specific bug
        onStart={() => {
          !autoplay && setShowCover(false);
        }}
        // without this, local videos will get slightly cropped
        config={{
          file: {
            attributes: {
              style: { overflow: 'hidden', width: '100%', height: '100%' },
            },
          },
        }}
        //add style overflow:hidden specifically for local video
        onReady={setActiveAutoplay}
      />
      {/* TODO if this is just cover it throws an error. Why does it have to check for localfile too? I think this is a newer issue */}
      {cover?.localFile && (
        <div
          className={`${coverWrapper} ${showCover ? show : ''} ${
            useCoverSize ? '' : 'mediaBoxInner'
          }`}
          role="button"
          onClick={handlePlayBtn}
          // I really only want keyboard-users to use the actual button, but js-lint is mad if I don't include these
          tabIndex="-1"
          onKeyDown={handleKeyDown}
          aria-label="Play Video"
        >
          <GatsbyImage
            className={`${imgWrapper}`}
            image={getImage(cover.localFile)}
            alt={''} // I believe cover image should be considered 'decorative'
          />
          {!autoplay && (
            <div className={`${playBtnWrapper}`}>
              {isVr ? <VrIcon /> : <PlayIcon />}
            </div>
          )}
          {autoplay && <div className={`${loadingSpinner}`} ref={spinnerRef} />}
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
